import {
  buildActionPlanItems,
  getActionPlanPageCount,
} from "./actionPlanItems";
import {
  computePhotoPageCount,
  computeShowCompliancePage,
} from "./pageCounts";
import {
  DOUBLE_DOOR_CONFIG_ID,
  PAGE_TYPES,
} from "./pageTypes";
import { mergeChunkCache } from "../cache/chunkMerge";

function nextPageNum(counter) {
  return ++counter.value;
}

function buildStaticPages(staticData, counter, includeCommunal) {
  const pages = [];

  pages.push({
    id: "cover",
    type: PAGE_TYPES.COVER,
    pageNum: nextPageNum(counter),
    chunkId: "static",
  });

  pages.push({
    id: "introduction",
    type: PAGE_TYPES.INTRODUCTION,
    pageNum: nextPageNum(counter),
    chunkId: "static",
  });

  pages.push({
    id: "company-profile",
    type: PAGE_TYPES.COMPANY_PROFILE,
    pageNum: nextPageNum(counter),
    chunkId: "static",
  });

  pages.push({
    id: "company-profile-info",
    type: PAGE_TYPES.COMPANY_PROFILE_INFO,
    pageNum: nextPageNum(counter),
    chunkId: "static",
  });

  pages.push({
    id: "summary",
    type: PAGE_TYPES.SUMMARY,
    pageNum: nextPageNum(counter),
    chunkId: "static",
  });

  if (includeCommunal) {
    const doorList =
      staticData?.propertyInspectionReport?.doorSummaryDto ?? [];
    const communalPageSize = 4;
    const communalPageCount = Math.ceil(doorList.length / communalPageSize);
    for (let i = 0; i < communalPageCount; i++) {
      pages.push({
        id: `communal-doors-${i}`,
        type: PAGE_TYPES.COMMUNAL_DOORS,
        pageNum: nextPageNum(counter),
        chunkId: "static",
        meta: { communalPageIndex: i },
      });
    }
  }

  return pages;
}

function buildDoorPagesForChunk(chunkId, chunkData, staticData, counter) {
  const pages = [];
  const doors = chunkData?.propertyInspectionReport?.combinedDoors ?? [];
  if (!doors.length) return pages;

  const allCompliances =
    chunkData?.propertyInspectionReport?.complianceChecks ?? [];
  const allPhysical =
    chunkData?.propertyInspectionReport?.physicalMeasurements ?? [];
  const allInspected =
    chunkData?.propertyInspectionReport?.inspectedDoors ?? [];
  const allAdditional =
    chunkData?.propertyInspectionReport?.additionalInfos ?? [];
  const allSummary = chunkData?.propertyInspectionReport?.doorSummaryDto ?? [];

  doors.forEach((door) => {
    const doorId = door.inspectedDoorId;
    const complianceList = allCompliances.filter(
      (x) => x.inspectedDoorId === doorId,
    );
    const physicalList = allPhysical.filter((x) => x.doorId === doorId);
    const originalDoor = allInspected.find((x) => x.inspectedDoorId === doorId);
    const additionalPhotos = allAdditional.find(
      (x) => x.inspectedDoorId === doorId,
    );
    const doorSummary = allSummary.find((x) => x.doorId === doorId);

    const isDoubleDoor =
      door?.doorConfigurationId === DOUBLE_DOOR_CONFIG_ID;

    if (isDoubleDoor) {
      pages.push({
        id: `door-detail-double-${doorId}-0`,
        type: PAGE_TYPES.DOOR_DETAIL_DOUBLE,
        pageNum: nextPageNum(counter),
        chunkId,
        doorId,
        meta: { doubleLeafIndex: 0 },
      });
      pages.push({
        id: `door-detail-double-${doorId}-1`,
        type: PAGE_TYPES.DOOR_DETAIL_DOUBLE,
        pageNum: nextPageNum(counter),
        chunkId,
        doorId,
        meta: { doubleLeafIndex: 1 },
      });
    } else {
      pages.push({
        id: `door-detail-${doorId}`,
        type: PAGE_TYPES.DOOR_DETAIL,
        pageNum: nextPageNum(counter),
        chunkId,
        doorId,
      });
    }

    const photoPageCount = computePhotoPageCount(
      originalDoor,
      physicalList,
      complianceList,
      additionalPhotos,
    );

    for (let i = 0; i < photoPageCount; i++) {
      pages.push({
        id: `door-photo-${doorId}-${i}`,
        type: PAGE_TYPES.DOOR_PHOTO,
        pageNum: nextPageNum(counter),
        chunkId,
        doorId,
        meta: { photoPageIndex: i, photoPageCount },
      });
    }

    const showRemedial = computeShowCompliancePage(
      door,
      complianceList,
      physicalList,
    );

    if (showRemedial) {
      const remedialsPerPage = 3;
      const remedialPages = doorSummary?.totalActionItems
        ? Math.ceil(doorSummary.totalActionItems / remedialsPerPage)
        : 0;

      for (let i = 0; i < remedialPages; i++) {
        pages.push({
          id: `door-remedial-${doorId}-${i}`,
          type: PAGE_TYPES.DOOR_REMEDIAL,
          pageNum: nextPageNum(counter),
          chunkId,
          doorId,
          meta: { remedialPageIndex: i, remedialPageCount: remedialPages },
        });
      }
    }
  });

  return pages;
}

function buildActionPlanPages(mergedReport, counter) {
  const pages = [];
  const actionItems = buildActionPlanItems(mergedReport);
  const totalPages = getActionPlanPageCount(actionItems);

  for (let i = 0; i < totalPages; i++) {
    pages.push({
      id: `action-plan-${i}`,
      type: PAGE_TYPES.ACTION_PLAN,
      pageNum: nextPageNum(counter),
      chunkId: "action-plan",
      meta: { actionPlanPageIndex: i, actionPlanPageCount: totalPages },
    });
  }

  return pages;
}

/**
 * @param {Record<number, object>} chunkCache
 * @param {{ allChunksLoaded: boolean }} options
 */
export function buildPageModel(chunkCache, options = {}) {
  const { allChunksLoaded = false } = options;
  const staticData = chunkCache[1];
  if (!staticData) return [];

  const counter = { value: 0 };
  const includeCommunal =
    staticData?.propertyInspectionReport?.companyProfile
      ?.includeCommunalDoorsInPdf ?? false;

  const pages = buildStaticPages(staticData, counter, includeCommunal);

  const chunkIds = Object.keys(chunkCache)
    .map(Number)
    .filter((n) => !Number.isNaN(n) && n >= 1)
    .sort((a, b) => a - b);

  chunkIds.forEach((chunkId) => {
    pages.push(
      ...buildDoorPagesForChunk(chunkId, chunkCache[chunkId], staticData, counter),
    );
  });

  if (allChunksLoaded) {
    const merged = mergeChunkCache(chunkCache);
    pages.push(...buildActionPlanPages(merged, counter));
  } else {
    pages.push({
      id: "action-plan-loading",
      type: PAGE_TYPES.ACTION_PLAN_LOADING,
      pageNum: nextPageNum(counter),
      chunkId: "static",
    });
  }

  return pages;
}

export function getChunkIdForPageIndex(pageModel, pageIndex) {
  const page = pageModel[pageIndex];
  if (!page) return 1;
  if (page.chunkId === "static" || page.chunkId === "action-plan") return 1;
  return page.chunkId;
}
